(function () {
    'use strict';

    const DESKTOP_QUERY = '(min-width: 64rem)';
    const navbarControllers = new WeakMap();

    function enhanceNavbar(navbar, documentObject, windowObject) {
        if (navbar?.hasAttribute?.('data-am-navbar-enhanced')) {
            return navbarControllers.get(navbar) ?? null;
        }

        const toggle = navbar.querySelector('[data-am-nav-toggle]');
        const navigation = navbar.querySelector('[data-am-primary-navigation]');

        if (!toggle || !navigation) {
            return null;
        }

        const horoscopeToggle = navbar.querySelector('[data-am-horoscope-toggle]');
        const horoscopeMenu = navbar.querySelector('[data-am-horoscope-menu]');
        const accountToggle = navbar.querySelector('[data-am-account-toggle]');
        const accountMenu = navbar.querySelector('[data-am-account-menu]');
        const accountFallback = navbar.querySelector('[data-am-account-fallback]');
        const compactSubscriptionAction = navbar.querySelector(
            '[data-am-subscription-action-compact]'
        );
        const desktopSubscriptionLink = navbar.querySelector(
            '[data-am-subscription-action-desktop] a[href]'
        );
        const desktopMedia = windowObject.matchMedia(DESKTOP_QUERY);
        const firstDestination = navigation.querySelector('a[href]');
        let toggleWasLastFocused = false;

        function accountIsExpanded() {
            return accountToggle?.getAttribute('aria-expanded') === 'true';
        }

        function setAccountExpanded(expanded, returnFocus = false) {
            if (!accountToggle || !accountMenu) {
                return;
            }

            accountMenu.hidden = !expanded;
            accountToggle.setAttribute('aria-expanded', String(expanded));

            if (returnFocus) {
                accountToggle.focus();
            }
        }

        function horoscopeIsExpanded() {
            return horoscopeToggle?.getAttribute('aria-expanded') === 'true';
        }

        function setHoroscopeExpanded(expanded, returnFocus = false) {
            if (!horoscopeToggle || !horoscopeMenu) {
                return;
            }

            const menuIsExpanded = desktopMedia.matches && expanded;

            horoscopeMenu.hidden = desktopMedia.matches && !menuIsExpanded;
            horoscopeToggle.setAttribute('aria-expanded', String(menuIsExpanded));

            if (returnFocus) {
                horoscopeToggle.focus();
            }
        }

        function compactMenuIsExpanded() {
            return !desktopMedia.matches && toggle.getAttribute('aria-expanded') === 'true';
        }

        function setExpanded(expanded, returnFocus = false) {
            const menuIsExpanded = !desktopMedia.matches && expanded;

            navigation.hidden = !desktopMedia.matches && !menuIsExpanded;
            toggle.setAttribute('aria-expanded', String(menuIsExpanded));
            toggle.setAttribute('aria-label', menuIsExpanded ? 'Fechar menu' : 'Abrir menu');

            if (!menuIsExpanded) {
                setAccountExpanded(false);
                setHoroscopeExpanded(false);
            }

            if (returnFocus) {
                toggle.focus();
            }
        }

        function navigationContainsFocus() {
            return typeof navigation.contains === 'function'
                && navigation.contains(documentObject.activeElement);
        }

        function handleToggle() {
            setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
        }

        function handleHoroscopeToggle(event) {
            event?.stopPropagation?.();
            setAccountExpanded(false);
            setHoroscopeExpanded(!horoscopeIsExpanded());
        }

        function handleAccountToggle(event) {
            event?.stopPropagation?.();
            setHoroscopeExpanded(false);
            setAccountExpanded(!accountIsExpanded());
        }

        function handleNavigation(event) {
            if (!event.target.closest?.('a[href]')) {
                return;
            }

            const returnFocus = compactMenuIsExpanded();
            const accountFocusWillBeHidden = accountIsExpanded()
                && accountMenu?.contains?.(documentObject.activeElement);
            const horoscopeFocusWillBeHidden = horoscopeIsExpanded()
                && horoscopeMenu?.contains?.(documentObject.activeElement);

            setAccountExpanded(false, accountFocusWillBeHidden);
            setHoroscopeExpanded(false, horoscopeFocusWillBeHidden);
            setExpanded(false, returnFocus);
        }

        function handleDocumentClick(event) {
            const target = event.target;
            const accountFocusWillBeHidden = accountMenu?.contains?.(documentObject.activeElement);
            const horoscopeFocusWillBeHidden = horoscopeMenu?.contains?.(documentObject.activeElement);
            const clickIsInsideAccount = accountToggle?.contains?.(target)
                || accountMenu?.contains?.(target);
            const clickIsInsideHoroscope = horoscopeToggle?.contains?.(target)
                || horoscopeMenu?.contains?.(target);

            if (accountIsExpanded() && !clickIsInsideAccount) {
                setAccountExpanded(false, accountFocusWillBeHidden);
            }

            if (horoscopeIsExpanded() && !clickIsInsideHoroscope) {
                setHoroscopeExpanded(false, horoscopeFocusWillBeHidden);
            }

            if (compactMenuIsExpanded() && !navbar.contains?.(target)) {
                setExpanded(false, navigationContainsFocus());
            }
        }

        function handleKeydown(event) {
            if (event.key !== 'Escape') {
                return;
            }

            if (accountIsExpanded()) {
                event.preventDefault();
                setAccountExpanded(false, true);
                return;
            }

            if (horoscopeIsExpanded()) {
                event.preventDefault();
                setHoroscopeExpanded(false, true);
                return;
            }

            if (compactMenuIsExpanded()) {
                event.preventDefault();
                setExpanded(false, true);
            }
        }

        function handleFocusIn(event) {
            toggleWasLastFocused = event.target === toggle;
        }

        function handleViewportChange() {
            const accountFocusWillBeHidden = desktopMedia.matches
                && accountMenu?.contains?.(documentObject.activeElement);
            const horoscopeFocusWillBeHidden = desktopMedia.matches
                && horoscopeMenu?.contains?.(documentObject.activeElement);
            const accountFallbackFocusWillBeHidden = desktopMedia.matches
                && accountFallback?.contains?.(documentObject.activeElement);
            const compactSubscriptionFocusWillBeHidden = desktopMedia.matches
                && compactSubscriptionAction?.contains?.(documentObject.activeElement);
            const compactFocusWillBeHidden = !desktopMedia.matches && navigationContainsFocus();
            const toggleWillBeHidden = desktopMedia.matches
                && (documentObject.activeElement === toggle || toggleWasLastFocused);

            setAccountExpanded(false);
            setHoroscopeExpanded(false);
            setExpanded(false, compactFocusWillBeHidden);

            if (compactFocusWillBeHidden) {
                return;
            }

            if (accountFallbackFocusWillBeHidden) {
                (accountToggle ?? firstDestination)?.focus();
            } else if (compactSubscriptionFocusWillBeHidden) {
                (desktopSubscriptionLink ?? firstDestination)?.focus();
            } else if (accountFocusWillBeHidden) {
                accountToggle?.focus();
            } else if (horoscopeFocusWillBeHidden) {
                horoscopeToggle?.focus();
            } else if (toggleWillBeHidden) {
                toggleWasLastFocused = false;
                firstDestination?.focus();
            }
        }

        navbar.setAttribute('data-am-navbar-enhanced', '');
        toggle.addEventListener('click', handleToggle);
        navigation.addEventListener('click', handleNavigation);
        horoscopeToggle?.addEventListener('click', handleHoroscopeToggle);
        accountToggle?.addEventListener('click', handleAccountToggle);
        documentObject.addEventListener('click', handleDocumentClick);
        documentObject.addEventListener('keydown', handleKeydown);
        documentObject.addEventListener('focusin', handleFocusIn);

        if (typeof desktopMedia.addEventListener === 'function') {
            desktopMedia.addEventListener('change', handleViewportChange);
        } else {
            desktopMedia.addListener(handleViewportChange);
        }

        setExpanded(false);
        setAccountExpanded(false);
        setHoroscopeExpanded(false);

        const controller = {
            close(returnFocus = false) {
                setAccountExpanded(false);
                setHoroscopeExpanded(false);
                setExpanded(false, returnFocus);
            },
            destroy() {
                toggle.removeEventListener('click', handleToggle);
                navigation.removeEventListener('click', handleNavigation);
                horoscopeToggle?.removeEventListener('click', handleHoroscopeToggle);
                accountToggle?.removeEventListener('click', handleAccountToggle);
                documentObject.removeEventListener('click', handleDocumentClick);
                documentObject.removeEventListener('keydown', handleKeydown);
                documentObject.removeEventListener('focusin', handleFocusIn);

                if (typeof desktopMedia.removeEventListener === 'function') {
                    desktopMedia.removeEventListener('change', handleViewportChange);
                } else {
                    desktopMedia.removeListener(handleViewportChange);
                }

                navbar.removeAttribute('data-am-navbar-enhanced');
                navbarControllers.delete(navbar);
            },
        };

        navbarControllers.set(navbar, controller);

        return controller;
    }

    function initNavbars(documentObject = document, windowObject = window) {
        if (typeof documentObject.querySelectorAll !== 'function') {
            return [];
        }

        return Array.from(documentObject.querySelectorAll('[data-am-navbar]'))
            .map(navbar => enhanceNavbar(navbar, documentObject, windowObject))
            .filter(Boolean);
    }

    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => initNavbars(), {once: true});
        } else {
            initNavbars();
        }
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {DESKTOP_QUERY, enhanceNavbar, initNavbars};
    }
})();
